import { DisplayValue, getEntryUrl, IHashMap, isNullOrUndefined } from '@adaptiva/core/common';
import { Summarizable } from '@adaptiva/core/summary';
import { BaseEditorComponent, BaseEditorError, CloudUser, ECacheMode, EditorInput, EditorState, EEditorErrorType, EFetchMode, EJavaClassNames, ENotificationTypes, EObjectType, IBaseEditorError, IEditorState, IObjectTableListConfiguration, ServerObject } from '@adaptiva/data-service';
import { AuthoringPropertyTab, ContentOverrideForRepairProperties, ContentOverrideForUninstallationProperties, ContentProperties, EPlatform, ESourceType, GeneralProperties, HousekeepingProperties, ICdnFile, ICdnUploadContent, IMetadataObject, MasFormService, MetadataAuthoringManagerService, MetadataAuthoringProperties, MetadataObject, MetadataSerialization, RelationshipsProperties, SoftwarePatch, SoftwareProduct, SoftwareRelease, SoftwareReleaseGroup } from '@adaptiva/metadata/common';
import { AfterViewInit, Directive, HostBinding, inject, OnInit } from '@angular/core';
import { Params } from '@angular/router';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, debounceTime, filter, forkJoin, map, Observable, of, Subject, switchMap, take, throwError } from 'rxjs';
import { AuthoringPropertyTabsComponent } from '../authoring-property-tabs';
import { IAuthoringPropertyOutput } from '../interfaces';
import { MetadataHistoryObjectRow } from '../metadata-history-object-table';

@Directive()
export abstract class BaseMetadataEditorComponent<T extends MetadataObject> extends BaseEditorComponent<T> implements OnInit, AfterViewInit {

	public abstract editorClass: typeof Summarizable;

	public abstract metadataAuthoringTabs: AuthoringPropertyTabsComponent<T>;

	public abstract tabNames: AuthoringPropertyTab[];

	public availableProperties: BehaviorSubject<string[]> = new BehaviorSubject([]);

	public browserConfig: IObjectTableListConfiguration;

	public canEdit$: BehaviorSubject<boolean> = new BehaviorSubject(false);

	public cdnUploadOptionsInstallation: BehaviorSubject<ICdnFile> = new BehaviorSubject(null);

	public cdnUploadOptionsRepair: BehaviorSubject<ICdnFile> = new BehaviorSubject(null);

	public cdnUploadOptionsUninstallation: BehaviorSubject<ICdnFile> = new BehaviorSubject(null);

	public createChildOptions: DisplayValue[];

	public editorHeight: string = '100%';

	public inheritedAuthoringPropertyMap: IHashMap;

	public javaClassNames: typeof EJavaClassNames = EJavaClassNames;

	public saveMetadataObjectAsName: string = '';

	public additionalNotificationTypes: string[] = [`MetadataObject${ENotificationTypes.AFTER_OBJECT_MODIFIED}`];

	public override disabledCssClasses: string[] = [];

	@HostBinding('class')
	protected readonly hostCssClasses: Record<string, boolean> = { 'ada-full-height': true };

	protected availablePropertiesUpdate$: Subject<void> = new Subject();

	protected saveAndComplete: boolean = false;

	protected targetPropertyMap: 'authoringProperties' | 'unrestrictedProperties' = 'authoringProperties';

	protected override queryParameters: Params = { 'javaClassName': '' };

	private _authoringPropertyErrorMap: Map<string, BaseEditorError[]> = new Map();

	private get _errorLevel(): EEditorErrorType {
		return this.saveAndComplete ? EEditorErrorType.Error : EEditorErrorType.Warning;
	}

	protected metadataAuthoring: MetadataAuthoringManagerService = inject(MetadataAuthoringManagerService);

	protected masFormService: MasFormService = inject(MasFormService);

	public override ngOnInit(): void {
		super.ngOnInit();

		this.browserConfig = {
			objectClassName: this.className as EJavaClassNames,
			parentFolderId: 0,
			sortColumn: 'name',
			ascending: true
		};

		this._debounceAvailablePropertiesUpdate();
		this.createChildOptions = BaseMetadataEditorComponent.HIERARCHY.get(this.editorClass) || [];
		// Set Editor Height
		this._setEditorHeight();
	}

	public override ngAfterViewInit(): void {
		super.ngAfterViewInit();

		if (this.metadataAuthoringTabs) {
			this.globalEditorService.activeEditorState.pipe(
				filter((_state: IEditorState<T>) => !isNullOrUndefined(_state?.selectedErrorChange)),
				switchMap((_state: IEditorState<T>) => _state.selectedErrorChange),
				this.takeUntilDestroyed()
			).subscribe({
				next: (_error: IBaseEditorError) => {
					this.metadataAuthoringTabs.setSelectedTab(_error?.content[0] || -1); // Non verbose, but -1 means don't select tab
				}
			});
		}
	}

	public override commitModelToView(): void {
		this._checkIfUserCanEdit();
	}

	protected _deserializeAuthoringProperties(_properties: IHashMap<string>): IHashMap<any> {
		return MetadataSerialization.deserialize(...MetadataSerialization.parse(_properties));
	}

	protected _getParentObjectIdsFromRouter(): int[] {
		const _baseUrl: string = getEntryUrl(this.router, this.route);
		const _splitUrl: int[] = _baseUrl.split('/').filter(x => !!x && !isNaN(+x)).map(x => +x);

		return _splitUrl;
	}

	protected _updateAvailableProperties(_properties: IHashMap<string>): void {
		const _propertyList: string[] = (_properties && Object.keys(_properties)) || [];

		this.availableProperties.next(_propertyList);
	}

	public pasteProperties(_updatedObject: T): void {
		this.state.track(_updatedObject);

		if (!isNullOrUndefined(_updatedObject?.authoringProperties?.General?.Name))
			this.state.set('name', _updatedObject.authoringProperties.General.Name);

		if (this.commitModelToView)
			this.commitModelToView();

		this.snackbarService.display('Pasted properties');
		this.cd.detectChanges();
	}

	public override generateNewObject(_input: EditorInput<T>): Observable<T> {
		return super.generateNewObject(_input).pipe(
			map((_newObject: T) => {
				return this.deserialize(_newObject);
			})
		);
	}

	public override generateServerObject(_input: EditorInput<T>): Observable<T> {
		return super.generateServerObject(_input).pipe(
			map((_serverObject: T) => {
				return this.deserialize(_serverObject);
			})
		);
	}

	public override onState(_state: EditorState<T>): void {
		super.onState(_state);

		if (!this.state.value.parentObjectId) {
			const _parentIds: int[] = this._getParentObjectIdsFromRouter();
			const _parentId: int = _parentIds?.pop() || 0;
			let _productId: int = _parentId;

			if (_parentIds.length) {
				_productId = _parentIds.shift();

				if (_productId && this.editorClass === SoftwareRelease) {
					const _relationships: RelationshipsProperties = _state.value?.[this.targetPropertyMap]?.Relationships;

					_relationships.Product = _productId;

					this.setAuthoringProperty({
						propertyObject: _relationships,
						propertyConstructor: RelationshipsProperties
					});
				}
			}

			if (this.state.value.isChild())
				this.state.value.setParentObjectId(_parentId);
		}

		this._initializeInheritedPropertyMap();
		this._checkIfUserCanEdit();
		this.cd.detectChanges();
	}

	private _checkIfUserCanEdit(): void {
		this.metadataAuthoring.canEdit(this.state.value).pipe(
			take(1)
		).subscribe({
			next: _canEdit => {
				this.canEdit$.next(_canEdit);
			}
		});
	}

	protected deserialize(_serverObject: T): T {
		const _object: T = this.summarize.generate<T>(this.className, _serverObject);

		// update completion items before deserializing authoring properties
		if (!MetadataSerialization.isDeserialized(_object)) {
			const _deserializedProperties: IHashMap = this._deserializeAuthoringProperties(_object[this.targetPropertyMap] || {});

			_object[this.targetPropertyMap.toString()] = MetadataSerialization.getInitializedAuthoringProperties<T>(
				this.editorClass,
				_deserializedProperties
			);
		}

		this._updateAvailableProperties(
			MetadataSerialization.serialize(
				this.editorClass,
				...Object.values(_object[this.targetPropertyMap])
			)
		);

		return _object;
	}

	private _initializeInheritedPropertyMap(): void {
		const _emptyInheritanceMap: Record<string, any> = {
			General: {},
			Housekeeping: {},
			Risk: {},
			Rules: {},
			Media: {},
			Relationships: {},
			Content: {},
			ContentForRepair: {},
			ContentForUninstallation: {},
			Install: {},
			Uninstall: {},
			Realtime: {},
			Extensions: {},
			UserPortal: {},
			Icon: {},
			InstallTime: {},
		};

		if (this.state.value?.parentObjectId) {
			this._getParentAuthoringProperties(this.state.value).subscribe({
				next: _inheritedProperties => {
					Object.keys(_emptyInheritanceMap).forEach(_namespace => {
						if (!_inheritedProperties.hasOwnProperty(_namespace))
							_inheritedProperties[_namespace] = {};
					});

					this.inheritedAuthoringPropertyMap = _inheritedProperties;
					this.cd.detectChanges();

					const _parentPlatform: EPlatform = _inheritedProperties.General.Platform;
					const _childPlatform: EPlatform = MetadataSerialization.getPropertyValue(this.state.value, 'General', 'Platform', this.targetPropertyMap);

					if (_parentPlatform !== _childPlatform) {
						MetadataSerialization.setPropertyValue(this.state.value, 'General', 'Platform', _parentPlatform, this.targetPropertyMap);
						this.state.check();
					}
				},
				error: _err => {
					this.logger.error(
						'BaseMetadataEditor._initializeInheritedPropertyMap',
						'Failed to create Inherited Property Map'
					)();
					this.inheritedAuthoringPropertyMap = _emptyInheritanceMap;
				}
			});
		} else
			this.inheritedAuthoringPropertyMap = _emptyInheritanceMap;
	}

	public revertMetadataObject(_metadataHistoryState: MetadataHistoryObjectRow): void {
		const _housekeeping: HousekeepingProperties = cloneDeep(this.state.value[this.targetPropertyMap].Housekeeping);
		const _oldObject: T = this.deserialize(_metadataHistoryState.object as T);

		_oldObject[this.targetPropertyMap].Housekeeping = MetadataSerialization.construct(this.editorClass, HousekeepingProperties, _housekeeping);

		this.state.track(_oldObject);
		this.commitModelToView?.();
		this.snackbarService.display('Reverted metadata object to ' + _metadataHistoryState.commitId);
	}

	public setAuthoringProperty<K extends MetadataAuthoringProperties = GeneralProperties>(_propertyObjectOutput: IAuthoringPropertyOutput<K>): void {
		const _propertyName: string = _propertyObjectOutput.propertyConstructor.namespace;

		if (_propertyName === 'General')
			this.state.set('name', _propertyObjectOutput.propertyObject['Name']);

		this.state.update(this.targetPropertyMap, (_currentPropertyMap: IHashMap<any>) => {
			if (!_currentPropertyMap)
				_currentPropertyMap = {};

			_currentPropertyMap[_propertyName] = _propertyObjectOutput.propertyObject;

			return _currentPropertyMap;
		});
	}

	private _getParentAuthoringProperties(_metadataObject: IMetadataObject, _authoringProperties?: IHashMap): Observable<IHashMap> {
		const _parentClassName: string = MetadataSerialization.getParentClassName(_metadataObject);

		return this.objectManager.getObjectById<IMetadataObject>(
			_parentClassName,
			_metadataObject.parentObjectId,
			EObjectType.VISIBLE,
			EFetchMode.COMPLETE,
			ECacheMode.CLONE
		).pipe(
			take(1),
			switchMap(_parentObject => {
				if (_parentObject) {
					const _deserializedProperties: IHashMap = this._deserializeAuthoringProperties(_parentObject.authoringProperties);

					if (_authoringProperties) {
						Object.keys(_deserializedProperties).forEach(_namespace => {
							if (!_authoringProperties.hasOwnProperty(_namespace))
								_authoringProperties[_namespace] = _deserializedProperties[_namespace];
							else {
								Object.keys(_deserializedProperties[_namespace]).forEach(_key => {
									if (!_authoringProperties[_namespace].hasOwnProperty(_key))
										_authoringProperties[_namespace][_key] = _deserializedProperties[_namespace][_key];
								});
							}
						});
					} else
						_authoringProperties = _deserializedProperties;

					if (_parentObject?.parentObjectId)
						return this._getParentAuthoringProperties(_parentObject, _authoringProperties);

					return of(_authoringProperties);
				}

				return throwError(() => 'BaseMetadataEditorComponent._initializeInheritedPropertyMap: Failed to fetch Parent Object');
			})
		);
	}

	/**
	 * Updating available properties involves serializing the entire map,
	 * so we guard the expensive logic behind a debouncer
	 */
	private _debounceAvailablePropertiesUpdate(): void {
		this.availablePropertiesUpdate$.pipe(
			debounceTime(2000),
			this.takeUntilDestroyed()
		).subscribe({
			next: () => {
				if (this.state.value && MetadataSerialization.isDeserialized(this.state.value), this.targetPropertyMap) {
					this._updateAvailableProperties(MetadataSerialization.serialize(
						this.editorClass,
						...Object.values(this.state.value[this.targetPropertyMap])
					));
				}
			}
		});
	}

	private _setEditorHeight(): void {
		this.metadataAuthoring.isAuthor().pipe(take(1)).subscribe({
			next: _state => {
				if (_state) {
					// Conditionally offset height of Header Area
					this.editorHeight = 'calc(100% - 72px)';
				}
			},
			error: _err => {
				this.logger.error('BaseMetadataEditor.ngOnInit', 'Failed to check Authoring status of User')();
			}
		});
	}

	public canComplete(_errorType: EEditorErrorType): Observable<boolean> {
		return this._validationRequest(_errorType).pipe(
			take(1),
			map((_errors: BaseEditorError[]) => {
				this.state.errors.next(_errors);

				return _errors.length === 0;
			})
		);
	}

	public override beforeSave(): Observable<T> {
		return this._validationRequest(this._errorLevel).pipe(
			switchMap((_errors: IBaseEditorError[]) => {
				this.state.errors.next(_errors);

				if ((this.saveAndComplete && _errors.length > 0) || !this.state.value.name) {
					this.saveAndComplete = false;

					return of(null);
				}

				return this.authService.user.pipe(
					map((_user: CloudUser) => {
						if (
							!MetadataSerialization.getPropertyValue<HousekeepingProperties>(
								this.state.value, 'Housekeeping', 'CreatedBy'
							) && !this.state.value.objectId
						) {
							MetadataSerialization.setPropertyValue<HousekeepingProperties>(
								this.state.value, 'Housekeeping', 'CreatedBy', _user.loginId
							);
							MetadataSerialization.setPropertyValue<HousekeepingProperties>(
								this.state.value, 'Housekeeping', 'CheckedOutState', true
							);
							MetadataSerialization.setPropertyValue<HousekeepingProperties>(
								this.state.value, 'Housekeeping', 'CheckedOutBy', _user.loginId
							);
							MetadataSerialization.setPropertyValue<HousekeepingProperties>(
								this.state.value, 'Housekeeping', 'UpdatedBy', _user.loginId
							);
						}

						return this.state.value;
					})
				);
			})
		);
	}

	private _validationRequest(_errorType: EEditorErrorType): Observable<BaseEditorError[]> {
		const _errors: BaseEditorError[] = this.validate();

		return this._updateAuthoringErrorMap(_errorType).pipe(map(() => {
			// This will update when asynchronous calls are completed
			const _errorKeys: string[] = Array.from(this._authoringPropertyErrorMap.keys());
			const _authoringPropertyErrors: BaseEditorError[] = _errorKeys.reduce<BaseEditorError[]>((_authErrors, _key) => {
				return _authErrors.concat(this._authoringPropertyErrorMap.get(_key));
			}, []);

			return [..._authoringPropertyErrors, ..._errors];
		}));
	}

	private _updateAuthoringErrorMap(_errorType: EEditorErrorType): Observable<null> {
		this._authoringPropertyErrorMap.clear();

		const _requiredMap: Map<string, Set<string>> = MetadataSerialization.getRequiredMap(this.editorClass);

		_requiredMap?.forEach((_properties: Set<string>, _namespace: string) => {
			const _namespaceErrors: BaseEditorError[] = [..._properties].reduce<BaseEditorError[]>((_errors: BaseEditorError[], _property: string) => {
				if (isNullOrUndefined(MetadataSerialization.getPropertyValue(this.state.value, _namespace, _property))) {
					const _inheritedRequiredProperty: any = this.inheritedAuthoringPropertyMap[_namespace]?.[_property];

					if (isNullOrUndefined(_inheritedRequiredProperty))
						_errors.push(new BaseEditorError(_errorType, 0, [_namespace, `Please enter a value for ${_property}`]));
				}

				return _errors;
			}, []);

			this._authoringPropertyErrorMap.set(_namespace, _namespaceErrors);
		});

		this._validateContentPropertyValues(ContentProperties.namespace, _errorType);
		this._validateContentPropertyValues(ContentOverrideForRepairProperties.namespace, _errorType);
		this._validateContentPropertyValues(ContentOverrideForUninstallationProperties.namespace, _errorType);

		// Validate forms:
		return this.masFormService.validateRenderedForm(this.state.value, EEditorErrorType.Error).pipe(
			map((_errorMap: IHashMap<BaseEditorError[]>) => {
				Object.entries(_errorMap).forEach(([_ns, _errors]: [string, BaseEditorError[]]) => {
					this._updatePropertyErrors(_ns, _errors);
				});

				return null;
			})
		);
	}

	private _validateContentPropertyValues(_namespace: string, _errorType: EEditorErrorType): void {
		const _requiredMap: Map<string, Set<string>> = MetadataSerialization.getRequiredMap(this.editorClass);
		const _propertyErrors: BaseEditorError[] = [];
		const _sourceType: ESourceType = MetadataSerialization.getPropertyValue<ESourceType, ContentProperties>(
			this.state.value, _namespace, 'SourceType'
		);
		const _contentSourceType: ESourceType = MetadataSerialization.getPropertyValue<ESourceType, ContentProperties>(
			this.state.value, ContentProperties.namespace, 'SourceType'
		);

		if (
			_requiredMap?.get(_namespace)?.has('SourceType') || (
				!isNaN(_sourceType) &&
				_sourceType > 0 &&
				_sourceType !== _contentSourceType
			)
		) {
			// AdaptivaUrl
			if (_sourceType === ESourceType.ADAPTIVA_CDN) {
				const _adaptivaUrl: string = MetadataSerialization.getPropertyValue<string, ContentProperties>(
					this.state.value, _namespace, 'AdaptivaUrl'
				);

				if (!_adaptivaUrl) {
					_propertyErrors.push(new BaseEditorError(_errorType, 1, [
						_namespace, 'Please enter a value for Adaptiva URL'
					]));
				}
			}

			const _validateTypes: ESourceType[] = [
				ESourceType.ADAPTIVA_CDN,
				ESourceType.VENDOR_CDN,
				ESourceType.VENDOR_CDN_WITH_BACKUP
			];

			if (_validateTypes.includes(_sourceType)) {
				// FileName
				const _fileName: string = MetadataSerialization.getPropertyValue<string, ContentProperties>(
					this.state.value, _namespace, 'FileName'
				);
				const _inheritedFileName: string = this.inheritedAuthoringPropertyMap[_namespace]?.['FileName'];

				if (!_fileName && !_inheritedFileName) {
					_propertyErrors.push(new BaseEditorError(_errorType, 1, [
						_namespace, 'Please enter a value for File Name'
					]));
				}
				// VendorUrl
				const _vendorUrl: string = MetadataSerialization.getPropertyValue<string, ContentProperties>(
					this.state.value, _namespace, 'VendorUrl'
				);

				if (!_vendorUrl && _sourceType !== ESourceType.ADAPTIVA_CDN) {
					_propertyErrors.push(new BaseEditorError(_errorType, 1, [
						_namespace, 'Please enter a value for Vendor URL'
					]));
				}

				const _contentSize: int = MetadataSerialization.getPropertyValue<int, ContentProperties>(
					this.state.value, _namespace, 'Size'
				);

				if (!_contentSize) {
					_propertyErrors.push(new BaseEditorError(_errorType, 1, [
						_namespace, 'Please enter a value for Size'
					]));
				}

				this._updatePropertyErrors(_namespace, _propertyErrors);
			}
		}
	}

	/**
	 * Helper method to update values in the authoring property map
	 * @param _namespace The namespace for the errors
	 * @param _values The errors to add or set.
	 */
	private _updatePropertyErrors(_namespace: string, _values: any): void {
		let _authErrors: BaseEditorError[] = this._authoringPropertyErrorMap.get(_namespace);

		if (_authErrors?.length)
			_authErrors = _authErrors.concat(_values);
		else
			this._authoringPropertyErrorMap.set(_namespace, _values);
	}

	public saveMetadataObject(): void {
		this.globalEditorService.saveCurrentEditor().subscribe();
	}

	public setSaveAndComplete(e: boolean): void {
		this.saveAndComplete = e;
	}

	public override afterSave(_metadataObject: T): void {
		const _deserialized: T = this.deserialize(_metadataObject);

		super.afterSave(_deserialized);

		if (this.saveAndComplete) {
			this.metadataAuthoring.completeObject(this.state.value.objectId).pipe(
				take(1)
			).subscribe({
				next: () => {
					this.snackbarService.display('Successfully submitted for approval');
				},
				error: e => {
					const _message: string = this.logger.extractErrorMessage(e);

					this.overlayService.error(
						'Failed to submit for approval',
						`Unexpected failure submitting ${this.state.value.name} for approval:\n\n${_message}`
					);
					this.logger.error(
						'BaseMetadataEditorComponent.afterSave',
						_message
					)();
				}
			});
		}

		this.saveAndComplete = false;
	}

	public clearUploadSubjects(): void {
		forkJoin({
			installation: this.cdnUploadOptionsInstallation.pipe(take(1)),
			repair: this.cdnUploadOptionsRepair.pipe(take(1)),
			uninstallation: this.cdnUploadOptionsUninstallation.pipe(take(1))
		}).pipe(take(1)).subscribe((_content$: ICdnUploadContent) => {
			if (_content$.installation != null)
				this.cdnUploadOptionsInstallation.next(null);

			if (_content$.repair != null)
				this.cdnUploadOptionsRepair.next(null);

			if (_content$.uninstallation != null)
				this.cdnUploadOptionsUninstallation.next(null);
		});
	}

	public mapListIds(_list: ServerObject[]): long[] {
		return _list.map(_item => +_item.objectId);
	}

}

export namespace BaseMetadataEditorComponent {

	export const HIERARCHY: Map<typeof Summarizable, DisplayValue[]> = new Map([
		[SoftwareProduct, [
			new DisplayValue('Software Patch', SoftwarePatch),
			new DisplayValue('Software Release Group', SoftwareReleaseGroup)
		]],
		[SoftwareReleaseGroup, [
			new DisplayValue('Software Release', SoftwareRelease)
		]]
	]);

}
